import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Search, X } from "lucide-react";
import FieldConfigurationAPI from "../../../lib/api/FieldConfiguration";
import "./style.css";

// Searchable picker for a payroll department code — mirrors
// SearchableGlAccountSelect's control/dropdown UX.
export default function SearchablePayrollDepartmentSelect({
  label,
  required,
  value,
  companyUuid,
  onChange,
  className = "",
  placeholder = "Search for payroll department...",
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  // Defaults to the picker until proven otherwise, so a slow/failed check
  // doesn't strand the field with nothing rendered.
  const [catalogHasData, setCatalogHasData] = useState(true);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);

  // A company that hasn't set up Payroll Departments yet would otherwise show
  // a picker with zero results and no way to enter a code — fall back to a
  // plain text input, matching the pre-picker behavior, when confirmed empty.
  useEffect(() => {
    let cancelled = false;
    const checkCatalog = async () => {
      try {
        const result = await FieldConfigurationAPI.getPayrollDepartments(
          "",
          companyUuid
        );
        if (!cancelled && result.status === 200 && Array.isArray(result.data)) {
          setCatalogHasData(result.data.length > 0);
        }
      } catch (error) {
        console.error("Error checking payroll department data:", error);
      }
    };
    checkCatalog();
    return () => {
      cancelled = true;
    };
  }, [companyUuid]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // The control can sit inside a scrollable modal body, so the dropdown is
  // portaled to <body> and positioned via the control's rect rather than
  // relying on CSS absolute positioning, which a scrolling ancestor clips.
  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      if (!dropdownRef.current) return;
      const rect = dropdownRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen]);

  useEffect(() => {
    const fetchDepartments = async () => {
      if (!isOpen) return;

      setLoading(true);
      try {
        const result = await FieldConfigurationAPI.getPayrollDepartments(
          searchTerm,
          companyUuid
        );
        if (result.status === 200 && Array.isArray(result.data)) {
          setDepartments(result.data);
        }
      } catch (error) {
        console.error("Error fetching payroll departments:", error);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(fetchDepartments, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, isOpen, companyUuid]);

  useEffect(() => {
    if (value && (!selectedDepartment || selectedDepartment.code !== value)) {
      const loadSelectedDepartment = async () => {
        try {
          const result = await FieldConfigurationAPI.getPayrollDepartments(
            value,
            companyUuid
          );
          if (result.status === 200 && Array.isArray(result.data)) {
            const match = result.data.find((d) => d.code === value);
            if (match) setSelectedDepartment(match);
          }
        } catch (error) {
          console.error("Error loading selected payroll department:", error);
        }
      };
      loadSelectedDepartment();
    } else if (!value && selectedDepartment) {
      setSelectedDepartment(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, companyUuid]);

  const handleSelect = (department) => {
    setSelectedDepartment(department);
    onChange(department.code);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedDepartment(null);
    onChange("");
    setSearchTerm("");
  };

  const formatDepartmentLabel = (department) =>
    department.description
      ? `${department.code} - ${department.description}`
      : department.code;

  if (!catalogHasData) {
    return (
      <div className={`searchable-payroll-department-select ${className}`}>
        {label && (
          <label className="searchable-payroll-department-select__label">
            {label}
            {required && <span className="danger">&nbsp;*</span>}
          </label>
        )}
        <input
          value={value || ""}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    );
  }

  return (
    <div
      className={`searchable-payroll-department-select ${className}`}
      ref={dropdownRef}
    >
      {label && (
        <label className="searchable-payroll-department-select__label">
          {label}
          {required && <span className="danger">&nbsp;*</span>}
        </label>
      )}

      <div
        className={`searchable-payroll-department-select__control ${
          isOpen ? "searchable-payroll-department-select__control--open" : ""
        } ${
          disabled
            ? "searchable-payroll-department-select__control--disabled"
            : ""
        }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="searchable-payroll-department-select__value">
          {selectedDepartment ? (
            <>
              <span>{formatDepartmentLabel(selectedDepartment)}</span>
              {!disabled && (
                <button
                  type="button"
                  className="searchable-payroll-department-select__clear"
                  onClick={handleClear}
                  aria-label="Clear selection"
                >
                  <X size={16} />
                </button>
              )}
            </>
          ) : value ? (
            <span>{value}</span>
          ) : (
            <span className="searchable-payroll-department-select__placeholder">
              {placeholder}
            </span>
          )}
        </div>
        <Search
          size={16}
          className="searchable-payroll-department-select__icon"
        />
      </div>

      {isOpen &&
        menuPos &&
        createPortal(
          <div
            ref={menuRef}
            className="searchable-payroll-department-select__dropdown"
            style={{
              position: "fixed",
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
            }}
          >
            <div className="searchable-payroll-department-select__search">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Type to search..."
                autoFocus
                className="searchable-payroll-department-select__search-input"
              />
            </div>

            <div className="searchable-payroll-department-select__options">
              {loading ? (
                <div className="searchable-payroll-department-select__loading">
                  Loading...
                </div>
              ) : departments.length === 0 ? (
                <div className="searchable-payroll-department-select__empty">
                  No payroll departments found
                </div>
              ) : (
                departments.map((department) => (
                  <div
                    key={department.uuid || department.code}
                    className="searchable-payroll-department-select__option"
                    onClick={() => handleSelect(department)}
                  >
                    <div className="searchable-payroll-department-select__option-code">
                      {department.code}
                    </div>
                    <div className="searchable-payroll-department-select__option-name">
                      {department.description}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
